/**
 * Modal for saving the current project as a reusable server-backed template.
 */

import { eventBus } from '../core/events.js';
import { formatBytes } from '../shared/format.js';
import { store } from '../core/state.js';
// NOT a static import (TL-2) — see the note in ui/preview-modal.js. shell.js
// statically imports this module, and preview-bundler.js drags the whole
// export graph onto the boot path behind it.
import { loadJSON } from '../core/asset-loader.js';
import { buildProjectZipBlob, extractTemplateMetadata } from '../templates/template-bundle.js';
import { slugifyTemplateName } from '../templates/template-slug.js';
import { getTemplateStatus, saveTemplate, TemplateApiError } from '../templates/template-api.js';
import { ensureAllPagesHydrated } from '../sections/section-manager.js';
import { escapeHTML } from '../shared/html-escape.js';
import {
  createModal as createKitModal,
  createButton,
  renderModalPhase,
  setButtonBusy
} from './components.js';

const MAX_TEMPLATE_ZIP_BYTES = 20 * 1024 * 1024;
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

function clear(el) {
  el.replaceChildren();
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createLocalShell() {
  // Delegates to the shared component-kit modal; preserves the local
  // { body, footer, close, isClosed } contract so the call site is unchanged.
  // `onClose` was added for CK-4: closing the modal must abort the in-flight
  // upload, not just stop rendering its result.
  let closed = false;
  const closeListeners = new Set();
  const kit = createKitModal({
    title: 'Save as template',
    size: null,
    overlayClassName: 'pb-template-save-overlay',
    onClose: () => {
      closed = true;
      for (const fn of closeListeners) {
        try {
          fn();
        } catch {
          // A listener must never block modal teardown.
        }
      }
      closeListeners.clear();
    },
    maxWidth: 620
  });
  return {
    body: kit.body,
    footer: kit.footer,
    close: kit.close,
    isClosed: () => closed,
    onClose: (fn) => {
      closeListeners.add(fn);
      return () => closeListeners.delete(fn);
    }
  };
}

function isAbortError(err) {
  return err?.name === 'AbortError';
}

function abortError() {
  if (typeof DOMException === 'function') return new DOMException('Aborted', 'AbortError');
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

async function loadIndustries() {
  try {
    const catalog = await loadJSON('data/template-catalog.json');
    const industries = new Set();
    for (const template of Array.isArray(catalog) ? catalog : []) {
      const industry = String(template?.industry || '')
        .trim()
        .toLowerCase();
      if (industry) industries.add(industry);
    }
    industries.add('other');
    return Array.from(industries).sort(compareIndustryLabels);
  } catch {
    return ['other'];
  }
}

function labelForIndustry(industry) {
  return String(industry || 'other')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function compareIndustryLabels(a, b) {
  const labelCompare = labelForIndustry(a).localeCompare(labelForIndustry(b), undefined, {
    sensitivity: 'base',
    numeric: true
  });
  return labelCompare || String(a || '').localeCompare(String(b || ''));
}

function renderError(body, footer, message, onRetry) {
  clear(body);
  clear(footer);
  const msg = document.createElement('p');
  msg.className = 'pb-confirm-msg';
  msg.textContent = message || 'Could not save template.';
  body.appendChild(msg);
  const closeBtn = createButton({
    variant: 'ghost',
    label: 'Close',
    onClick: () => document.querySelector('.pb-template-save-overlay .pb-modal-close')?.click()
  });
  const retryBtn = createButton({ variant: 'primary', label: 'Retry', onClick: onRetry });
  footer.append(closeBtn, retryBtn);
}

function renderSuccess(body, footer, result, status, thumbnailError = '') {
  clear(body);
  clear(footer);
  const wrap = document.createElement('div');
  wrap.style.display = 'grid';
  wrap.style.gap = '0.75rem';

  const title = document.createElement('p');
  title.className = 'pb-confirm-msg';
  title.textContent = 'Template saved.';
  wrap.appendChild(title);

  const slug = document.createElement('p');
  slug.className = 'pb-confirm-detail';
  slug.textContent = `Slug: ${result.slug}`;
  wrap.appendChild(slug);

  const screenshot = document.createElement('p');
  screenshot.className = 'pb-confirm-detail';
  const screenshotStatus = status?.screenshotStatus || result.screenshotStatus || 'pending';
  screenshot.textContent = thumbnailError
    ? `Thumbnail skipped: ${thumbnailError}`
    : `Thumbnail: ${screenshotStatus}`;
  wrap.appendChild(screenshot);

  body.appendChild(wrap);

  const doneBtn = createButton({
    variant: 'ghost',
    label: 'Done',
    onClick: () => document.querySelector('.pb-template-save-overlay .pb-modal-close')?.click()
  });
  const pickerBtn = createButton({
    variant: 'primary',
    label: 'Open Template Picker',
    onClick: () => {
      document.querySelector('.pb-template-save-overlay .pb-modal-close')?.click();
      eventBus.emit('toolbar:open-templates');
    }
  });
  footer.append(doneBtn, pickerBtn);
}

async function pollTemplateStatus(slug, shouldStop, onStatus) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let latest = null;
  while (!shouldStop() && Date.now() < deadline) {
    await delay(POLL_INTERVAL_MS);
    if (shouldStop()) break;
    latest = await getTemplateStatus(slug);
    onStatus(latest);
    if (['ready', 'failed', 'skipped'].includes(latest.screenshotStatus)) break;
  }
  return latest;
}

async function saveTemplateFlow({ name, industry, body, footer, isClosed, signal, onCancel }) {
  // Only the upload itself is natively abortable; the ZIP build and the preview
  // bundle are synchronous-ish work with no cancellation seam. Checking the flag
  // between phases means a cancel is honoured at the next boundary at worst,
  // instead of the flow running to completion and POSTing anyway.
  const stopIfCancelled = () => {
    if (signal?.aborted) throw abortError();
  };
  const cancelOpts = typeof onCancel === 'function' ? { onCancel } : {};

  let state = store.getState();
  if (!state) throw new Error('No project state to save.');

  if (state.pages?.some((p) => p._pendingTemplateRefs?.length)) {
    renderModalPhase(body, footer, {
      label: 'Loading remaining pages...',
      detail: 'Finalizing the full template before packaging.',
      ...cancelOpts
    });
    await ensureAllPagesHydrated();
    if (isClosed()) return;
    stopIfCancelled();
    state = store.getState();
    if (!state) throw new Error('No project state to save.');
  }

  renderModalPhase(body, footer, {
    label: 'Building template bundle...',
    detail: 'Packaging project state and referenced assets.',
    ...cancelOpts
  });
  const zipBlob = await buildProjectZipBlob(state);
  if (zipBlob.size > MAX_TEMPLATE_ZIP_BYTES) {
    throw new Error(
      `Reduce image sizes - template bundle is ${formatBytes(zipBlob.size)}, limit is 20 MB.`
    );
  }
  if (isClosed()) return;
  stopIfCancelled();

  renderModalPhase(body, footer, {
    label: 'Preparing thumbnail...',
    detail: `${formatBytes(zipBlob.size)} template bundle ready.`,
    ...cancelOpts
  });
  let html = '';
  let thumbnailError = '';
  try {
    // Now that the bundler takes a signal, cancel reaches INTO the thumbnail
    // build instead of waiting for it to finish and checking afterwards. The
    // abort must not be demoted to `thumbnailError` — it is not a thumbnail
    // problem, and stopIfCancelled() below is what should surface it.
    const { buildPreviewBundle } = await import('../preview/preview-bundler.js');
    const bundle = await buildPreviewBundle(state, { signal });
    html = bundle.html;
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    thumbnailError = err?.message || 'Thumbnail bundle failed.';
  }
  if (isClosed()) return;
  stopIfCancelled();

  renderModalPhase(body, footer, {
    label: 'Uploading template...',
    detail: thumbnailError
      ? 'Template will save without a thumbnail for now.'
      : 'Thumbnail will generate in the background.',
    ...cancelOpts
  });
  const meta = {
    ...extractTemplateMetadata(state, { name, industry }),
    name,
    industry,
    projectName: state.projectName || name,
    thumbnailError
  };
  const result = await saveTemplate({ meta, zipBlob, html, signal });
  if (isClosed()) return;

  renderModalPhase(body, footer, {
    label: 'Generating thumbnail...',
    detail: 'The template is available now. Thumbnail may take a few seconds.'
  });
  renderSuccess(body, footer, result, result, thumbnailError);

  if (!thumbnailError && result.screenshotStatus === 'pending') {
    await pollTemplateStatus(result.slug, isClosed, (status) => {
      renderSuccess(body, footer, result, status, thumbnailError);
    });
  }
}

async function renderForm(body, footer, modalApi) {
  clear(body);
  clear(footer);
  const state = store.getState();
  const industries = await loadIndustries();
  if (modalApi.isClosed()) return;

  const defaultName =
    state?.projectName && state.projectName !== 'Untitled'
      ? state.projectName
      : 'Untitled Template';

  const form = document.createElement('form');
  form.style.display = 'grid';
  form.style.gap = '1rem';
  form.innerHTML = `
    <label style="display:grid;gap:0.35rem;">
      <span>Template name</span>
      <input class="pb-input" name="name" maxlength="80" value="">
      <span class="pb-confirm-detail" data-slug-preview></span>
      <span class="pb-confirm-detail" data-field-error="name" style="color:var(--pb-danger,#dc2626);"></span>
    </label>
    <label style="display:grid;gap:0.35rem;">
      <span>Industry</span>
      <select class="pb-input" name="industry">
        ${industries.map((industry) => `<option value="${escapeHTML(industry)}">${escapeHTML(labelForIndustry(industry))}</option>`).join('')}
      </select>
      <span class="pb-confirm-detail" data-field-error="industry" style="color:var(--pb-danger,#dc2626);"></span>
    </label>
  `;

  const nameInput = form.elements.name;
  const slugPreview = form.querySelector('[data-slug-preview]');
  nameInput.value = defaultName;
  const updateSlugPreview = () => {
    slugPreview.textContent = `Slug preview: ${slugifyTemplateName(nameInput.value)}`;
  };
  nameInput.addEventListener('input', updateSlugPreview);
  updateSlugPreview();

  body.appendChild(form);
  const cancelBtn = createButton({ variant: 'ghost', label: 'Cancel', onClick: modalApi.close });
  const saveBtn = createButton({ variant: 'primary', label: 'Save Template' });
  footer.append(cancelBtn, saveBtn);

  // Single-flight guard on the SHARED submit path, not on the button. There are
  // two entries into submit() — the Save button and the form's own submit event
  // (Enter in the name field) — and the form listener never consulted
  // saveBtn.disabled, so a second Enter started a second ZIP build and a second
  // POST. Guarding the button alone only ever closed one of the two doors.
  let submitting = false;

  async function submit() {
    if (submitting) return;
    for (const el of form.querySelectorAll('[data-field-error]')) el.textContent = '';
    const name = String(form.elements.name.value || '').trim();
    const industry = String(form.elements.industry.value || '').trim();
    let hasError = false;
    if (name.length < 2 || name.length > 80) {
      form.querySelector('[data-field-error="name"]').textContent = 'Use 2-80 characters.';
      hasError = true;
    }
    if (!industry) {
      form.querySelector('[data-field-error="industry"]').textContent = 'Choose an industry.';
      hasError = true;
    }
    if (hasError) return;

    // Set only after validation passes, so a rejected attempt does not lock the
    // form. saveBtn carries a plain textContent label, so the kit's busy
    // stash/restore is safe here (no interactive children to lose).
    submitting = true;
    setButtonBusy(saveBtn, true, { busyLabel: 'Saving…' });
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    // Closing the modal aborts the upload too — otherwise a 20 MB POST kept
    // running with nothing left to read its response.
    const detachClose = modalApi.onClose(() => controller?.abort());
    try {
      await saveTemplateFlow({
        name,
        industry,
        body,
        footer,
        isClosed: modalApi.isClosed,
        signal: controller?.signal,
        onCancel: controller ? () => controller.abort() : null
      });
    } catch (err) {
      if (modalApi.isClosed()) return;
      if (isAbortError(err)) {
        // User-initiated cancel is not a failure — go back to the form they
        // filled in rather than showing them an error they caused on purpose.
        void renderForm(body, footer, modalApi);
        return;
      }
      if (
        err instanceof TemplateApiError &&
        err.status >= 400 &&
        err.status < 500 &&
        err.data?.field
      ) {
        form.querySelector(`[data-field-error="${err.data.field}"]`).textContent = err.message;
        return;
      }
      renderError(body, footer, err?.message || 'Could not save template.', () => {
        void renderForm(body, footer, modalApi);
      });
    } finally {
      // Always release, even on the isClosed() early return above — the modal
      // is gone by then, but leaving the flag set would strand a re-opened form
      // if this closure were ever reused.
      detachClose();
      submitting = false;
      setButtonBusy(saveBtn, false);
    }
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submit();
  });
  saveBtn.addEventListener('click', () => void submit());
  nameInput.focus();
  nameInput.select();
}

export function showSaveTemplateModal() {
  const modalApi = createLocalShell();
  void renderForm(modalApi.body, modalApi.footer, modalApi);
}
